import React, { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import './App.css';
import ReactMarkdown from 'react-markdown';

// Configuration (could be moved to environment variables)
const API_BASE_URL = 'http://datascience.imtech.res.in/anshu/gutbot-api/api/v1';

function App() {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [userId, setUserId] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const messagesEndRef = useRef(null);

  // Initialize user ID on component mount
  useEffect(() => {
    const storedUserId = localStorage.getItem('gutbot_user_id');
    if (storedUserId) {
      setUserId(storedUserId);
    } else {
      const newUserId = uuidv4();
      localStorage.setItem('gutbot_user_id', newUserId);
      setUserId(newUserId);
    }
  }, []);

 // Create session and load message history when chat is opened
 useEffect(() => {
  const initializeChat = async () => {
    if (isChatOpen && userId) {
      // Check for existing session first
      const storedSessionId = localStorage.getItem('gutbot_session_id');
      
      if (storedSessionId) {
        setSessionId(storedSessionId);
        await fetchMessageHistory(userId, storedSessionId);
      } else {
        // Create new session if none exists
        const newSessionId = await createNewSession();
        if (newSessionId) {
          // No need to fetch history for a new session
          setMessages([{
            id: uuidv4(),
            sender: 'bot',
            text: "Welcome! Let's talk gut health. What's on your mind?",
            timestamp: new Date().toISOString()
          }]);
        }
      }
    }
  };
  
  initializeChat();
}, [isChatOpen, userId]);

  // Scroll to bottom of messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);


  // Fetch message history from the server
  const fetchMessageHistory = async (userId, sessionId) => {
    try {
      setIsLoadingHistory(true);

      const response = await axios.get(`${API_BASE_URL}/session/${sessionId}/conversation/`, {
          headers: { "Authorization" : `Bearer ${userId}` },
          maxRedirects: 0
      });

      if (response.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
        // Transform the response into our message format
        const formattedMessages = response.data.data.flatMap(msg => ([
          {
            id: msg.conversation_id || uuidv4(),
            sender: 'user',
            text: msg.question,
            timestamp: msg.created_at
          },
          {
            id: msg.conversation_id || uuidv4(),
            sender: 'bot',
            text: msg.answer,
            timestamp: msg.created_at
          }
        ]));

        setMessages(formattedMessages);
      } else {
        // If no messages, show welcome message
        setMessages([{
          id: uuidv4(),
          sender: 'bot',
          text: "Welcome back! How can I help with your gut health today?",
          timestamp: new Date().toISOString()
        }]);
      }

      setIsLoadingHistory(false);
    } catch (error) {
      console.error('Error fetching message history:', error);

      // Show welcome message with error indication
      setMessages([{
        id: uuidv4(),
        sender: 'bot',
        text: "Welcome! I couldn't retrieve our previous conversation. How can I help you today?",
        timestamp: new Date().toISOString()
      }]);

      setIsLoadingHistory(false);
    }
  };

  // Create a new session by calling the external API
  const createNewSession = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(`${API_BASE_URL}/session/`, { "initial_context": { "data": "" } }, {
        headers: { "Authorization" : `Bearer ${userId}`},
        maxRedirects: 0
      });

      setSessionId(response.data.data.session_id);

      // Store session ID in localStorage as backup
      localStorage.setItem('gutbot_session_id', response.data.data.session_id);

      // Add welcome message
      setMessages([{
        id: uuidv4(),
        sender: 'bot',
        text: "Welcome! Let's talk gut health. What's on your mind?",
        timestamp: new Date().toISOString()
      }]);

      setIsLoading(false);

    } catch (error) {
      console.error('Error creating session:', error);
      setIsLoading(false);
    }
  };

  // Handle sending messages
  const handleSendMessage = async (event) => {
    event.preventDefault();

    if (!inputValue.trim()) return;
    // Ensure we have a session ID
    let currentSessionId = localStorage.getItem('gutbot_session_id');

    // If still no session ID, create a new one
    if (!currentSessionId) {
      currentSessionId = await createNewSession();
      if (!currentSessionId) {
        // Handle session creation failure
        setMessages(prev => [...prev, {
          id: uuidv4(),
          sender: 'bot',
          text: "Sorry, I'm having trouble connecting. Please refresh and try again.",
          timestamp: new Date().toISOString(),
          isError: true
        }]);
        return;
      }
    } else {
      // Set the session ID from localStorage
      setSessionId(currentSessionId);
    }


    // Add user message to chat
    const userMessage = {
      id: uuidv4(),
      sender: 'user',
      text: inputValue,
      timestamp: new Date().toISOString()
    };

    setMessages(prevMessages => [...prevMessages, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Call API to get bot response
      const response = await axios.post(`${API_BASE_URL}/session/${currentSessionId}/conversation/`, { "question": inputValue }, {
        headers: { "Authorization" : `Bearer ${userId}`},
        maxRedirects: 0
      });

      // Add bot response to chat
      const botMessage = {
        id: uuidv4(),
        sender: 'bot',
        text: response.data.data.answer,
        timestamp: new Date().toISOString()
      };

      setMessages(prevMessages => [...prevMessages, botMessage]);
      setIsLoading(false);
    } catch (error) {
      console.error('Error sending message:', error);

      // Add error message
      const errorMessage = {
        id: uuidv4(),
        sender: 'bot',
        text: "Sorry, I'm having trouble connecting. Please try again later.",
        timestamp: new Date().toISOString(),
        isError: true
      };

      setMessages(prevMessages => [...prevMessages, errorMessage]);
      setIsLoading(false);
    }
  };

  // Handle quick reply buttons
  const handleQuickReply = (replyText) => {
    const syntheticEvent = { preventDefault: () => { } };
    setInputValue(replyText);
    handleSendMessage(syntheticEvent);
  };

  // Toggle chat open/closed
  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
  };

  // Format time for message display
  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="gut-bot-container">
      {!isChatOpen ? (
        <div className="gut-bot-collapsed" onClick={toggleChat}>
          <div className="gut-bot-icon">
            <img src="./logo-256.png" alt="Gut Bot" />
          </div>
          <div className="tooltip">
            <div className="tooltip-text">Hi there, I'm Gut bot!</div>
            <div className="tooltip-subtext">Let's talk about gut health</div>
          </div>
        </div>
      ) : (
        <div className="gut-bot-expanded">
          <div className="chat-header">
            <div className="header-icon">
              <img src="./logo-64.png" alt="Gut Bot" />
            </div>
            <div className="header-title">Gut Bot</div>
            {/* <div className="header-time">Today {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div> */}
          </div>

          <div className="chat-messages">
            {isLoadingHistory ? (
              <div className="loading-history">
                <div className="loading-spinner"></div>
                <p>Loading conversation history...</p>
              </div>
            ) : (
              <>
                {messages.map(message => (
                  <div key={message.id} className={`message ${message.sender}`}>
                    {message.sender === 'bot' && (
                      <div className="bot-avatar">
                        <img src="./logo-64.png" alt="Gut Bot" />
                      </div>
                    )}
                    <div className={`message-bubble ${message.isError ? 'error' : ''}`}>
                      <ReactMarkdown>{message.text}</ReactMarkdown>
                    </div>
                    <div ref={messagesEndRef} />
                  </div>
                ))}

                {isLoading && (
                  <div className="message bot">
                    <div className="bot-avatar">
                      <img src="./logo-64.png" alt="Gut Bot" />
                    </div>
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                )}

                {messages.length === 1 && messages[0].sender === 'bot' && (
                  <div className="quick-replies">
                    <button onClick={() => handleQuickReply("Why is gut health important?")}>
                      Why is gut health important?
                    </button>
                    <button onClick={() => handleQuickReply("What are gut microbes?")}>
                      What are gut microbes?
                    </button>
                    <button onClick={() => handleQuickReply("What are probiotics?")}>
                      What are probiotics?
                    </button>
                  </div>
                )}
              </>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form className="chat-input" onSubmit={handleSendMessage}>
            <input
              type="text"
              placeholder="Reply to Gut Bot"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              disabled={isLoading || isLoadingHistory}
            />
            <button 
              type="submit" 
              className="send-button"
              disabled={isLoading || isLoadingHistory || !inputValue.trim()}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {/* <button type="button" className="emoji-button">😊</button>
            <button type="button" className="settings-button">⚙️</button>
            <button type="button" className="attach-button">📎</button> */}
          </form>

          <div className="chat-footer">
            <span className="footer-text">Chatbot</span>
            <span className="footer-dot">•</span>
            <span className="footer-text">by</span>
            <a href="https://ab-lab.com" className="footer-link">AB-lab</a>
            <span className="footer-divider">|</span>
            <a href="/privacy" className="footer-link">Privacy Policy</a>
          </div>

          <button className="close-button" onClick={toggleChat}>×</button>
        </div>
      )}
    </div>
  );
}

export default App;
